import { useTheme } from "@amx/ux-twoui-theming";
import { Text as FluentText } from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../shared/logging";
import { TextBaseProps, validateProps } from "../../shared/props";
import { FontWeightOverride, getFontRampStyle, HeadingTypes } from "../../styles/fonts";

/** Array of `HeadingTag` values */
export const headingTags = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;

/** All heading tags `h1`-`h6` */
export type HeadingTag = typeof headingTags[number];

/** Array of `HeadingWeightOverride` values */
export const headingWeightOverrides: readonly Extract<FontWeightOverride, "bold">[] = ["bold"];

/** Weight overrides for `Heading` */
export type HeadingWeightOverride = typeof headingWeightOverrides[number];

export interface HeadingProps extends TextBaseProps {
    /**
     * tag is a HeadingTag and can be "h1", "h2", "h3", "h4", "h5", or "h6"
     */
    readonly tag: HeadingTag;
    /**
     * variant is Fontramp type, see the readme for the figma link
     */
    readonly variant?: HeadingTypes;
    /**
     * Optional override of the font-weight defined in the type ramp. Only
     * accepts "bold" currently as all Heading types already have `semi-bold`
     * font weight
     */
    readonly weightOverride?: HeadingWeightOverride;
    readonly children: string;
}

export const Heading: React.FC<HeadingProps> = ({
    tag,
    variant = "paneHeader",
    block,
    nowrap,
    weightOverride,
    colorOverride,
    children,
    ...otherProps
}) => {
    useComponentLogger("Heading", otherProps.id);

    validateProps("Heading", otherProps);
    const theme = useTheme();
    const styles = getFontRampStyle(theme, variant, weightOverride, colorOverride);

    return (
        <FluentText
            as={tag}
            block={block}
            nowrap={nowrap}
            styles={{
                root: {
                    fontSize: styles.fontSize,
                    fontWeight: styles.fontWeight,
                    lineHeight: styles.lineHeight,
                    fontFamily: styles.fontFamily,
                    selectors: styles.selectors,
                    color: styles.color
                }
            }}
            {...otherProps}
        >
            {children}
        </FluentText>

    );
};
